import React, { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { OrderProps } from "../../types/order";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getOrderDetails,
  updateOrderDetails,
} from "../../features/orderDetails/orderDetailsSlice";
import { POST } from "../../services/axios.service";

type ReceiveFormInput = {
  qty_field_1: number;
  weight_field_1: number;
  qty_field_2?: number;
  weight_field_2?: number;
  close_order: boolean;
};

type Props = {
  onSuccess?: () => void;
};

const OrderCloseForm = ({ onSuccess }: Props) => {
  const dispatch = useAppDispatch();
  const order = useAppSelector(getOrderDetails);

  const isInSet = order?.item?.isinset || false;

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<ReceiveFormInput>({
    defaultValues: {
      qty_field_1: undefined,
      weight_field_1: undefined,
      qty_field_2: undefined,
      weight_field_2: undefined,
      close_order: false,
    }
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const onSubmit: SubmitHandler<ReceiveFormInput> = async (data) => {
    setIsLoading(true);
    setRequestError(null);

    try {
      if (order && order.id) {
        const requestObj: any = {
          order_id: order.id,
          qty_field_1: data.qty_field_1 || 0,
          weight_field_1: data.weight_field_1 || 0,
          close_order: data.close_order || false,
        };

        if (isInSet) {
          requestObj.qty_field_2 = data.qty_field_2 || 0;
          requestObj.weight_field_2 = data.weight_field_2 || 0;
        }

        await POST("/v1/worker/receive-transactions", requestObj)
          .then(async (response: any) => {
            if (response.data.status === "success") {
              await reset();

              if (response.data.data?.order) {
                await dispatch(
                  updateOrderDetails(response.data.data.order as OrderProps)
                );
              }

              if (response.data.data?.pdf_url) {
                window.open(response.data.data.pdf_url, '_blank');
              }

              // Close the form after success
              if (onSuccess) {
                onSuccess();
              }
            } else {
              setRequestError(response.data.message);
            }
          })
          .catch((error: any) => {
            setRequestError(error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
          });
      } else {
        setRequestError("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
      }
    } catch (error) {
      setRequestError("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
    }

    setIsLoading(false);
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Error Message */}
        {requestError !== null && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-4 text-center">
            {requestError}
          </div>
        )}

        {/* Form Title */}
        <h3 className="text-xl font-bold text-primary-1 mb-4 text-center">
          ડિલિવરી માહિતી દાખલ કરો
        </h3>

        {/* Set Item Fields */}
        {isInSet && (
          <>
            {/* Set 1 Section - Kesh */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <h4 className="text-base font-bold text-primary-1 mb-3 text-center">
                કેશ <span className="text-gray-400 font-normal text-sm">(વૈકલ્પિક)</span>
              </h4>

              <div className="space-y-3">
                <div>
                  <label className="text-gray-600 font-medium text-sm mb-1 block">
                    ડિલિવરી નંગ
                  </label>
                  <input
                    className="w-full h-12 px-4 text-primary-1 font-semibold text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-2 focus:border-primary-1"
                    type="number"
                    step="1"
                    min="0"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    placeholder="કેશ નંગ દાખલ કરો"
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                    {...register("qty_field_2", {
                      min: 0,
                      valueAsNumber: true,
                    })}
                  />
                </div>

                <div>
                  <label className="text-gray-600 font-medium text-sm mb-1 block">
                    ડિલિવરી વજન (કિલો)
                  </label>
                  <input
                    className="w-full h-12 px-4 text-primary-1 font-semibold text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-2 focus:border-primary-1"
                    type="number"
                    step="0.0001"
                    min="0"
                    inputMode="decimal"
                    placeholder="કેશ વજન દાખલ કરો"
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                    {...register("weight_field_2", {
                      min: 0,
                      valueAsNumber: true,
                    })}
                  />
                </div>
              </div>
            </div>

            {/* Set 2 Section - Patta */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <h4 className="text-base font-bold text-primary-1 mb-3 text-center">
                પટ્ટા
              </h4>

              <div className="space-y-3">
                <div>
                  <label className="text-gray-600 font-medium text-sm mb-1 block">
                    ડિલિવરી નંગ
                  </label>
                  <input
                    className="w-full h-12 px-4 text-primary-1 font-semibold text-lg border-2 border-primary-1 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-2"
                    type="number"
                    step="1"
                    min="0"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    placeholder="પટ્ટા નંગ દાખલ કરો"
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                    {...register("qty_field_1", {
                      required: true,
                      min: 0,
                      valueAsNumber: true,
                    })}
                  />
                  {errors?.qty_field_1 && (
                    <span className="text-red-500 text-sm">ડિલિવરી નંગ દાખલ કરો</span>
                  )}
                </div>

                <div>
                  <label className="text-gray-600 font-medium text-sm mb-1 block">
                    ડિલિવરી વજન (કિલો)
                  </label>
                  <input
                    className="w-full h-12 px-4 text-primary-1 font-semibold text-lg border-2 border-primary-1 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-2"
                    type="number"
                    step="0.0001"
                    min="0"
                    inputMode="decimal"
                    placeholder="પટ્ટા વજન દાખલ કરો"
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                    {...register("weight_field_1", {
                      required: true,
                      min: 0,
                      valueAsNumber: true,
                    })}
                  />
                  {errors?.weight_field_1 && (
                    <span className="text-red-500 text-sm">ડિલિવરી વજન દાખલ કરો</span>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Regular Item Fields */}
        {!isInSet && (
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <div className="space-y-3">
              <div>
                <label className="text-gray-600 font-medium text-sm mb-1 block">
                  ડિલિવરી નંગ
                </label>
                <input
                  className="w-full h-12 px-4 text-primary-1 font-semibold text-lg border-2 border-primary-1 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-2"
                  type="number"
                  step="1"
                  min="0"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  placeholder="ડિલિવરી નંગ દાખલ કરો"
                  onFocus={(e) =>
                    e.target.addEventListener(
                      "wheel",
                      function (e) {
                        e.preventDefault();
                      },
                      { passive: false }
                    )
                  }
                  {...register("qty_field_1", {
                    required: true,
                    min: 0,
                    valueAsNumber: true,
                  })}
                />
                {errors?.qty_field_1 && (
                  <span className="text-red-500 text-sm">ડિલિવરી નંગ દાખલ કરો</span>
                )}
              </div>

              <div>
                <label className="text-gray-600 font-medium text-sm mb-1 block">
                  ડિલિવરી વજન (કિલો)
                </label>
                <input
                  className="w-full h-12 px-4 text-primary-1 font-semibold text-lg border-2 border-primary-1 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-2"
                  type="number"
                  step="0.0001"
                  min="0"
                  inputMode="decimal"
                  placeholder="ડિલિવરી વજન દાખલ કરો"
                  onFocus={(e) =>
                    e.target.addEventListener(
                      "wheel",
                      function (e) {
                        e.preventDefault();
                      },
                      { passive: false }
                    )
                  }
                  {...register("weight_field_1", {
                    required: true,
                    min: 0,
                    valueAsNumber: true,
                  })}
                />
                {errors?.weight_field_1 && (
                  <span className="text-red-500 text-sm">ડિલિવરી વજન દાખલ કરો</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Close Order Checkbox */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              id="close_order"
              className="w-5 h-5 accent-primary-1 rounded"
              {...register("close_order")}
            />
            <div>
              <span className="text-primary-1 font-semibold">ઓર્ડર પૂર્ણ કરો</span>
              <p className="text-xs text-gray-500 mt-0.5">
                ફક્ત ત્યારે જ ટિક કરો જ્યારે આ છેલ્લી ડિલિવરી હોય
              </p>
            </div>
          </label>
        </div>

        {/* Submit Button */}
        <button
          className="w-full py-4 rounded-xl text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold shadow-lg disabled:opacity-50"
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? "રાહ જુઓ..." : "સેવ કરો અને રસીદ જુઓ"}
        </button>
      </form>
    </div>
  );
};

export default OrderCloseForm;
